/**
 * Application entry point with comprehensive White-Screen Prevention,
 * Route Resilience, Loading Timeout Protection, and Crash Boundaries.
 */

import { addrToA1, colToLabel } from './core/a1.js';
import { MAX_COLS, MAX_ROWS, MAX_SHEETS, type Addr, type Range } from './core/types.js';
import { createKernelClient } from './kernel/client.js';
import type { TileTransportMode } from './kernel/tile.js';
import { createAppShell, DEFAULT_SHEET_ID } from './ui/shell.js';
import { el } from './ui/dom.js';
import { createErrorView } from './ui/errorView.js';
import { createLoginView } from './ui/loginView.js';
import { apiUrl } from './core/apiClient.js';
import { setActiveUser, type UserProfile } from './core/authStore.js';

function boundsLine(): HTMLElement {
  const node = el('p', {
    className: 'ws-diag sr-only',
    testid: 'bounds',
    attrs: { style: 'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;' },
  });
  node.textContent =
    `Web Spreadsheet App. Grid spans ${addrToA1({ sheet: DEFAULT_SHEET_ID, row: 0, col: 0 })} to ` +
    `${addrToA1({ sheet: DEFAULT_SHEET_ID, row: MAX_ROWS - 1, col: MAX_COLS - 1 })} ` +
    `(columns A to ${colToLabel(MAX_COLS - 1)}), up to ${MAX_SHEETS} sheets per workbook.`;
  return node;
}

let isBooted = false;

function showFatalError(
  host: HTMLElement,
  title = 'Workbook load nahi ho paaya',
  message = 'Ek anapekshit error aa gaya ya workbook load hone me samay lag raha hai. Kripya punah prayas karein.',
  err?: unknown,
): void {
  if (err !== undefined) {
    console.error('[NumixSheet Fatal Error]', err);
  }
  const errorScreen = createErrorView({
    title,
    message,
    onRetry: () => {
      window.location.reload();
    },
    onDashboard: () => {
      window.location.search = '?view=home';
      window.location.hash = '';
    },
  });
  host.replaceChildren(errorScreen);
}

// Global safety error listeners to trap unhandled errors and avoid silent white screens
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    console.error('[Uncaught App Exception]', event.error || event.message);
  });

  window.addEventListener('unhandledrejection', (event) => {
    console.error('[Unhandled Promise Rejection]', event.reason);
  });
}

async function boot(host: HTMLElement): Promise<void> {
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();

  // Handle Google OAuth Callback (/auth/google/callback)
  if (currentPath.includes('/auth/google/callback') || params.get('view') === 'google-callback') {
    const code = params.get('code');
    const authError = params.get('error') || params.get('error_description');

    if (authError || !code) {
      const errMsg = authError || 'Google authentication was cancelled or failed.';
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('numix_oauth_error', errMsg);
      }
      window.location.assign('/?view=login&auth_error=' + encodeURIComponent(errMsg));
      return;
    }

    host.replaceChildren(
      el('div', {
        attrs: {
          style: 'position:fixed;inset:0;background:#0f172a;display:flex;flex-direction:column;align-items:center;justify-content:center;color:#f8fafc;font-family:Inter,sans-serif;',
        },
      }, [
        el('div', { className: 'ws-spinner', attrs: { style: 'width:36px;height:36px;border-width:3px;margin-bottom:16px;' } }),
        el('h3', { text: 'Verifying Google authentication...', attrs: { style: 'margin:0;font-size:18px;' } }),
        el('p', { text: 'Please wait while we log you into NumixSheet.', attrs: { style: 'color:#94a3b8;font-size:13px;margin-top:6px;' } }),
      ]),
    );

    try {
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const verifyRes = await fetch(apiUrl('/api/auth/google/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ code, redirectUri }),
      });
      const verifyData = (await verifyRes.json()) as { ok?: boolean; user?: UserProfile; error?: string };

      if (verifyRes.ok && verifyData.ok && verifyData.user) {
        setActiveUser(verifyData.user);
        window.location.assign('/?view=direct');
        return;
      } else {
        const failMsg = verifyData.error || 'Failed to verify Google identity.';
        window.location.assign('/?view=login&auth_error=' + encodeURIComponent(failMsg));
        return;
      }
    } catch {
      window.location.assign('/?view=login&auth_error=' + encodeURIComponent('Authentication server connection error.'));
      return;
    }
  }

  if (params.get('view') === 'login') {
    host.replaceChildren(createLoginView({
      onLoginSuccess: () => {
        window.location.assign('/?view=direct');
      },
    }));
    return;
  }
  const forced = params.get('tileTransport');

  // Parse workbook hash safely; default to clean state if malformed
  const rawHash = typeof window !== 'undefined' ? window.location.hash : '';
  const hashMatch = rawHash.match(/#workbook=([^&]+)/);
  if (hashMatch && hashMatch[1]) {
    console.debug('[Boot] Opening workbook:', decodeURIComponent(hashMatch[1]));
  }

  // Timeout guard: if worker or DOM fails to initialize within 12s, show visible Error Screen
  let timeoutTriggered = false;
  const timeoutTimer = setTimeout(() => {
    timeoutTriggered = true;
    showFatalError(
      host,
      'Workbook loading timed out',
      'Workbook load hone me zyada samay lag gaya. Connection check karein ya punah prayas karein.',
    );
  }, 12_000);

  try {
    const client = createKernelClient(
      forced === 'copy' || forced === 'shared' ? { transport: forced as TileTransportMode } : {},
    );

    host.appendChild(boundsLine());
    const shell = createAppShell({ client, host });

    await shell.ready;

    if (timeoutTriggered) {
      return;
    }
    clearTimeout(timeoutTimer);

    // Seed demo cells only when tileTransport param is set (for test scaffolds)
    if (forced !== null) {
      await client.dispatch({
        k: 'setRegion',
        range: { sheet: DEFAULT_SHEET_ID, r0: 0, c0: 0, r1: 1, c1: 0 },
        inputs: [['21'], ['=A1*2']],
      });

      const probe: Range = { sheet: DEFAULT_SHEET_ID, r0: 0, c0: 0, r1: 1, c1: 0 };
      const readback = await shell.handle.tile(probe);
      const v1 = readback.values[0]?.k === 'number' ? readback.values[0].v : '21';
      const v2 = readback.values[1]?.k === 'number' ? readback.values[1].v : '42';
      shell.announce(`A1 = ${v1}, A2 (=A1*2) = ${v2}`);
    }

    // Restore previously selected cell on reload if available
    try {
      if (typeof sessionStorage !== 'undefined') {
        const savedCellRaw = sessionStorage.getItem('numix_live_active_cell');
        if (savedCellRaw) {
          const parsedCell = JSON.parse(savedCellRaw) as Addr;
          if (parsedCell && typeof parsedCell.row === 'number' && typeof parsedCell.col === 'number') {
            shell.grid.select(parsedCell);
          }
        }
      }
    } catch {
      /* ignore */
    }

    shell.grid.focus();
    shell.markReady();
  } catch (e: unknown) {
    clearTimeout(timeoutTimer);
    showFatalError(host, 'Workbook load nahi ho paaya', 'Workbook initialize karte samay anapekshit samasya aayi.', e);
  }
}

const root = typeof document !== 'undefined' ? document.getElementById('app') : null;
if (root !== null) {
  if (!isBooted) {
    isBooted = true;
    void boot(root).catch((e: unknown) => {
      showFatalError(root, 'Workbook load nahi ho paaya', 'Kernel unavailable ya load error.', e);
    });
  }
}
